import * as React from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PendingInterrupt } from "../chat-ui.types";

interface Props {
  pendingInterrupt: PendingInterrupt | null;
  onResolve: (documentIds: string[]) => void;
  onCancel: () => void;
}

export const InterruptDialog = ({ pendingInterrupt, onResolve, onCancel }: Props) => {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setSelected(new Set());
  }, [pendingInterrupt?.queryId]);

  if (!pendingInterrupt) {
    return null;
  }

  const { prompt, options } = pendingInterrupt.payload;

  const toggle = (documentId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select documents to search</DialogTitle>
          <DialogDescription>{prompt}</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 space-y-2 overflow-y-auto py-2">
          {options.map(option => (
            <label
              key={option.document_id}
              className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted"
            >
              <Checkbox
                checked={selected.has(option.document_id)}
                onCheckedChange={() => toggle(option.document_id)}
                className="mt-1"
              />
              <span className="flex flex-col">
                <span className="font-medium">{option.name}</span>
                {option.summary && <span className="text-sm text-muted-foreground">{option.summary}</span>}
              </span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel this turn
          </Button>
          <Button disabled={selected.size === 0} onClick={() => onResolve(Array.from(selected))}>
            Search selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
